import React from 'react';
import { Document, Page, Text, View, StyleSheet, pdf, Link } from '@react-pdf/renderer';
import { computeLoanTermYears, calcGuaranteeFee, calcAnnualPmt, calcProjectCost } from '../../utils/loanMath';

const styles = StyleSheet.create({
  page: {
    padding: 32,
    backgroundColor: '#FFFFFF',
    fontFamily: 'Helvetica',
    color: '#374151',
  },
  headerBanner: {
    backgroundColor: '#1E3A5F',
    padding: 10,
    marginBottom: 16,
    marginLeft: -32,
    marginRight: -32,
    marginTop: -32,
  },
  headerBannerText: {
    color: '#FFFFFF',
    fontSize: 11,
    textAlign: 'center',
  },
  headerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  companyName: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 2,
  },
  brokerText: {
    fontSize: 10,
    color: '#6B7280',
  },
  prequalifiedLabel: {
    fontSize: 10,
    color: '#6B7280',
    textAlign: 'right',
    marginBottom: 2,
  },
  prequalifiedAmount: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#0EA5E9',
  },
  section: {
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 6,
    color: '#1E3A5F',
  },
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 4,
    overflow: 'hidden',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 22,
  },
  tableRowLast: {
    flexDirection: 'row',
    minHeight: 22,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#F3F4F6',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tableHeaderCell: {
    padding: 6,
    color: '#374151',
    fontWeight: 'bold',
    fontSize: 9,
    textAlign: 'right',
    flex: 1,
  },
  tableHeaderCellLeft: {
    padding: 6,
    color: '#374151',
    fontWeight: 'bold',
    fontSize: 9,
    textAlign: 'left',
    width: '45%',
  },
  tableLabelCell: {
    width: '55%',
    padding: 6,
    fontSize: 9,
    color: '#374151',
    textAlign: 'left',
  },
  tableValueCell: {
    width: '45%',
    padding: 6,
    fontSize: 9,
    color: '#111827',
    textAlign: 'right',
  },
  boldCell: {
    fontWeight: 'bold',
  },
  greenText: {
    color: '#10B981',
  },
  redText: {
    color: '#EF4444',
  },
  highlightRow: {
    backgroundColor: '#F9FAFB',
  },
  analysisCard: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 4,
    padding: 10,
    backgroundColor: '#FAFAFA',
  },
  analysisCardTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1E3A5F',
    marginBottom: 2,
  },
  analysisCardDescription: {
    fontSize: 9,
    color: '#6B7280',
    marginBottom: 4,
  },
  analysisCardAmount: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#111827',
  },
  analysisCardEquity: {
    fontSize: 8,
    color: '#6B7280',
    marginTop: 2,
  },
  borrowerProfileRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 20,
  },
  borrowerProfileLabel: {
    width: '35%',
    padding: 5,
    fontSize: 9,
    fontWeight: 'bold',
    color: '#374151',
  },
  borrowerProfileValue: {
    width: '65%',
    padding: 5,
    fontSize: 9,
    color: '#6B7280',
  },
  lenderSection: {
    marginTop: 10,
    marginBottom: 8,
  },
  lenderTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1E3A5F',
    marginBottom: 4,
  },
  lenderText: {
    fontSize: 9,
    color: '#6B7280',
  },
  lenderBold: {
    fontWeight: 'bold',
    color: '#374151',
  },
  ctaButton: {
    backgroundColor: '#0EA5E9',
    padding: 12,
    borderRadius: 4,
    marginTop: 10,
  },
  ctaText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    bottom: 16,
    left: 32,
    right: 32,
    fontSize: 8,
    textAlign: 'center',
    color: '#9CA3AF',
  },
});

const PrequalSummaryDocument = ({ data }) => {
  const {
    businessName = 'N/A',
    prequalifiedAmount = 0,
    prequalificationType = 'Adjusted Purchase Price',
    prequalificationEquityPercent = 10,
    ocrData = {},
    brokerName = null,
    realEstateValue = 0,
    prequalId = null,
  } = data;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  // Calculate loan components using total project cost approach
  const equityPercent = prequalificationEquityPercent || 10;
  const sbaLoanPercent = 100 - equityPercent;
  const project = calcProjectCost(prequalifiedAmount, { equityPercent });

  const equityAmount = project.equity;
  const totalLoanAmount = project.loanAmount;
  const guaranteeFee = project.guaranteeFee;
  const packagingFee = project.packagingFee;
  const closingCosts = project.closingCosts;

  // Interest rate range: Prime (6.75%) + 2% to Prime + 2.75%
  const LOW_RATE = 0.095;
  const HIGH_RATE = 0.1025;
  const termYears = computeLoanTermYears({ purchasePrice: prequalifiedAmount, realEstateValue: Number(realEstateValue) });
  const annualDebtServiceLow = calcAnnualPmt({ principal: totalLoanAmount, rate: LOW_RATE, termYears });
  const annualDebtServiceHigh = calcAnnualPmt({ principal: totalLoanAmount, rate: HIGH_RATE, termYears });
  const monthlyPaymentLow = annualDebtServiceLow / 12;
  const monthlyPaymentHigh = annualDebtServiceHigh / 12;

  // Sort years chronologically
  const years = Object.keys(ocrData).sort((a, b) => {
    const order = {
      'Business Tax Return - 2 Years Ago': 0,
      'Business Tax Return - Last Year': 1,
      'Business Tax Return - Current Year': 2
    };
    return order[a] - order[b];
  });

  const safeNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) || !isFinite(num) ? 0 : num;
  };

  const calculateEBITDA = (yearData) => {
    if (!yearData) return 0;
    const income = safeNumber(yearData.ordinary_business_income);
    const officerComp = safeNumber(yearData.compensation_of_officers);
    const interest = safeNumber(yearData.interest);
    const depreciation = safeNumber(yearData.depreciation);
    const amortization = safeNumber(yearData.amortization);
    return income + officerComp + interest + depreciation + amortization;
  };

  const calculateAddBacks = (yearData) => {
    if (!yearData || !yearData.other_deductions) return 0;
    if (Array.isArray(yearData.other_deductions)) {
      return yearData.other_deductions.reduce((total, item) => total + safeNumber(item.amount), 0);
    }
    return 0;
  };

  const calculateSDE = (yearData) => {
    if (!yearData) return 0;
    return calculateEBITDA(yearData) + calculateAddBacks(yearData);
  };

  const calculateNOI = (yearData) => {
    if (!yearData) return 0;
    return calculateSDE(yearData);
  };

  const calculateDSCR = (yearData) => {
    const noi = calculateNOI(yearData);
    return annualDebtServiceHigh > 0 ? noi / annualDebtServiceHigh : 0;
  };

  // Get most recent year label
  const mostRecentYear = years.length > 0 ? years[years.length - 1] : null;
  const mostRecentYearLabel = mostRecentYear ? mostRecentYear.replace('Business Tax Return - ', '') : 'Most Recent Year';

  // Signup link for borrowers
  const signupLink = prequalId ? `https://lended.ai/prequal-signup/${prequalId}` : 'https://lended.ai';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Banner */}
        <View style={styles.headerBanner}>
          <Text style={styles.headerBannerText}>Lended - Prequalification Summary</Text>
        </View>

        {/* Header with Company Name and Prequalified Amount */}
        <View style={styles.headerSection}>
          <View style={styles.headerLeft}>
            <Text style={styles.companyName}>{businessName}</Text>
            {brokerName && (
              <Text style={styles.brokerText}>Listed by: {brokerName}</Text>
            )}
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.prequalifiedLabel}>Prequalified For:</Text>
            <Text style={styles.prequalifiedAmount}>{formatCurrency(prequalifiedAmount)}</Text>
          </View>
        </View>

        {/* Financing Structure */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Financing Structure</Text>
          <View style={styles.analysisCard}>
            <Text style={styles.analysisCardTitle}>{prequalificationType}</Text>
            <Text style={styles.analysisCardDescription}>
              {mostRecentYearLabel} DSCR at 1.25 or above ({sbaLoanPercent}% SBA / {equityPercent}% Equity)
            </Text>
            <Text style={styles.analysisCardAmount}>{formatCurrency(prequalifiedAmount)}</Text>
            <Text style={styles.analysisCardEquity}>
              Equity Required: {formatCurrency(equityAmount)} ({equityPercent}% of total project cost)
            </Text>
          </View>
        </View>

        {/* Project Cost & Loan Terms */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Project Cost & Loan Terms</Text>
          <Text style={{ fontSize: 8, color: '#6B7280', marginBottom: 4, marginTop: -4 }}>Backed by lender. Subject to change per borrower strength</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <Text style={styles.tableLabelCell}>Purchase Price</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(prequalifiedAmount)}</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.tableLabelCell}>+ SBA Guarantee Fee</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(guaranteeFee)}</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.tableLabelCell}>+ Packaging Fee</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(packagingFee)}</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.tableLabelCell}>+ Closing Costs</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(closingCosts)}</Text>
            </View>
            <View style={[styles.tableRow, styles.highlightRow]}>
              <Text style={[styles.tableLabelCell, styles.boldCell]}>Total Project Cost</Text>
              <Text style={[styles.tableValueCell, styles.boldCell]}>{formatCurrency(project.totalProjectCost)}</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.tableLabelCell}>Equity Injection ({equityPercent}%)</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(equityAmount)}</Text>
            </View>
            <View style={[styles.tableRow, styles.highlightRow]}>
              <Text style={[styles.tableLabelCell, styles.boldCell]}>SBA Loan ({sbaLoanPercent}%)</Text>
              <Text style={[styles.tableValueCell, styles.boldCell]}>{formatCurrency(totalLoanAmount)}</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={[styles.tableLabelCell, styles.boldCell]}>Interest Rate</Text>
              <Text style={styles.tableValueCell}>Prime + 2% - Prime + 2.75%</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={[styles.tableLabelCell, styles.boldCell]}>Loan Term</Text>
              <Text style={styles.tableValueCell}>{termYears} years</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={[styles.tableLabelCell, styles.boldCell]}>Monthly Payment</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(monthlyPaymentLow)} - {formatCurrency(monthlyPaymentHigh)}</Text>
            </View>
            <View style={styles.tableRowLast}>
              <Text style={[styles.tableLabelCell, styles.boldCell]}>Annual Debt Service</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(annualDebtServiceLow)} - {formatCurrency(annualDebtServiceHigh)}</Text>
            </View>
          </View>
        </View>

        {/* Financial Summary */}
        {years.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Financial Summary</Text>
            <View style={styles.table}>
              <View style={styles.tableHeaderRow}>
                <Text style={styles.tableHeaderCellLeft}>Metric</Text>
                {years.map((year, idx) => (
                  <Text key={idx} style={styles.tableHeaderCell}>
                    {year.replace('Business Tax Return - ', '')}
                  </Text>
                ))}
              </View>

              <View style={styles.tableRow}>
                <Text style={styles.tableLabelCell}>Gross Receipts</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  return (
                    <Text key={idx} style={styles.tableValueCell}>
                      {formatCurrency(yearData.gross_receipts_or_sales || yearData.gross_receipts || 0)}
                    </Text>
                  );
                })}
              </View>

              <View style={styles.tableRow}>
                <Text style={styles.tableLabelCell}>COGS</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  return (
                    <Text key={idx} style={styles.tableValueCell}>
                      {formatCurrency(yearData.cost_of_goods_sold || 0)}
                    </Text>
                  );
                })}
              </View>

              <View style={styles.tableRow}>
                <Text style={styles.tableLabelCell}>Ordinary Business Income</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  return (
                    <Text key={idx} style={styles.tableValueCell}>
                      {formatCurrency(yearData.ordinary_business_income || 0)}
                    </Text>
                  );
                })}
              </View>

              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, styles.boldCell]}>EBITDA</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  return (
                    <Text key={idx} style={[styles.tableValueCell, styles.boldCell]}>
                      {formatCurrency(calculateEBITDA(yearData))}
                    </Text>
                  );
                })}
              </View>

              <View style={styles.tableRow}>
                <Text style={styles.tableLabelCell}>Other Addbacks</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  return (
                    <Text key={idx} style={styles.tableValueCell}>
                      {formatCurrency(calculateAddBacks(yearData))}
                    </Text>
                  );
                })}
              </View>

              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, styles.boldCell]}>SDE</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  return (
                    <Text key={idx} style={[styles.tableValueCell, styles.boldCell]}>
                      {formatCurrency(calculateSDE(yearData))}
                    </Text>
                  );
                })}
              </View>

              <View style={styles.tableRow}>
                <Text style={styles.tableLabelCell}>Annual Debt Service</Text>
                {years.map((year, idx) => (
                  <Text key={idx} style={styles.tableValueCell}>
                    {formatCurrency(annualDebtServiceLow)} - {formatCurrency(annualDebtServiceHigh)}
                  </Text>
                ))}
              </View>

              <View style={styles.tableRowLast}>
                <Text style={[styles.tableLabelCell, styles.boldCell]}>DSCR</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const dscr = calculateDSCR(yearData);
                  return (
                    <Text key={idx} style={[styles.tableValueCell, styles.boldCell, dscr >= 1.25 ? styles.greenText : styles.redText]}>
                      {formatNumber(dscr)}
                    </Text>
                  );
                })}
              </View>
            </View>
          </View>
        )}

        {/* Ideal Borrower Profile */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Ideal Borrower Profile</Text>
          <View style={styles.table}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCellLeft, { width: '35%' }]}>Category</Text>
              <Text style={[styles.tableHeaderCell, { textAlign: 'left', width: '65%' }]}>Details</Text>
            </View>
            <View style={styles.borrowerProfileRow}>
              <Text style={styles.borrowerProfileLabel}>Industry Experience</Text>
              <Text style={styles.borrowerProfileValue}>5+ years in related industry</Text>
            </View>
            <View style={styles.borrowerProfileRow}>
              <Text style={styles.borrowerProfileLabel}>Cash Available</Text>
              <Text style={styles.borrowerProfileValue}>{formatCurrency(equityAmount * 1.1)}+ recommended</Text>
            </View>
            <View style={styles.borrowerProfileRow}>
              <Text style={styles.borrowerProfileLabel}>Post-Transaction Liquidity</Text>
              <Text style={styles.borrowerProfileValue}>{formatCurrency(equityAmount * 0.5)}+ remaining</Text>
            </View>
            <View style={styles.borrowerProfileRow}>
              <Text style={styles.borrowerProfileLabel}>Management Capability</Text>
              <Text style={styles.borrowerProfileValue}>Ability to oversee operations</Text>
            </View>
            <View style={[styles.borrowerProfileRow, { borderBottomWidth: 0 }]}>
              <Text style={styles.borrowerProfileLabel}>Credit Profile</Text>
              <Text style={styles.borrowerProfileValue}>Strong credit history (680+)</Text>
            </View>
          </View>
        </View>

        {/* Lender Support Section */}
        <View style={styles.lenderSection}>
          <Text style={styles.lenderTitle}>Supported by Multiple SBA Lenders</Text>
          <Text style={styles.lenderText}>
            This prequalification reflects interest from multiple independent SBA lenders. Final approval subject to full underwriting review.
          </Text>
        </View>

        {/* CTA Button */}
        <Link src={signupLink} style={{ textDecoration: 'none' }}>
          <View style={styles.ctaButton}>
            <Text style={styles.ctaText}>Get Prequalified for This Listing</Text>
          </View>
        </Link>

        {/* Footer */}
        <Text style={styles.footer}>
          Generated on {new Date().toLocaleDateString()} | Lended Prequalification Summary
        </Text>
      </Page>
    </Document>
  );
};

export const generatePrequalSummaryPDF = async (data) => {
  try {
    const blob = await pdf(<PrequalSummaryDocument data={data} />).toBlob();
    return blob;
  } catch (error) {
    console.error('Error generating prequalification PDF:', error);
    throw error;
  }
};
